import api from '../../../utils/api';
import { getOrCreateMessagingDeviceId } from '../../../utils/messagingIdentity';
import { sendEncryptedUserMessage } from '../../../utils/messageSendE2ee';

export const messagingService = {
  // Conversations
  getConversations: async () => {
    const response = await api.get('/messaging/conversations/');
    return response.data;
  },

  getConversation: async (conversationId) => {
    const response = await api.get(`/messaging/conversations/${conversationId}/`);
    return response.data;
  },

  createConversation: async (conversationData) => {
    const response = await api.post('/messaging/conversations/', conversationData);
    return response.data;
  },

  updateConversation: async (conversationId, conversationData) => {
    const response = await api.patch(`/messaging/conversations/${conversationId}/`, conversationData);
    return response.data;
  },

  deleteConversation: async (conversationId) => {
    await api.delete(`/messaging/conversations/${conversationId}/`);
  },

  unhideConversation: async (conversationId) => {
    const response = await api.post(`/messaging/conversations/${conversationId}/unhide_conversation/`);
    return response.data;
  },
  hideConversation: async (conversationId) => {
    const response = await api.post(`/messaging/conversations/${conversationId}/hide_conversation/`);
    return response.data;
  },

  clearConversationUntilNow: async (conversationId) => {
    const response = await api.post(`/messaging/conversations/${conversationId}/clear_until_now/`);
    return response.data;
  },

  // Messages
  getMessages: async (conversationId, params = {}) => {
    const device_id = getOrCreateMessagingDeviceId();
    const response = await api.get(`/messaging/conversations/${conversationId}/messages/`, {
      params: { ...params, device_id },
    });
    return response.data;
  },

  sendMessage: async (messageData) => {
    const device_id = getOrCreateMessagingDeviceId();
    const response = await api.post('/messaging/messages/', messageData, {
      headers: { 'X-Yona-Messaging-Device-ID': device_id },
    });
    return response.data;
  },

  registerMessagingPublicKey: async (publicKeyB64) => {
    await api.post('/messaging/user-keys/', {
      public_key: publicKeyB64,
      device_id: getOrCreateMessagingDeviceId(),
    });
  },

  lookupMessagingPublicKeys: async (userIds) => {
    if (!userIds || userIds.length === 0) return {};
    const q = userIds.map(String).join(',');
    const response = await api.get(
      `/messaging/user-keys/lookup/?user_ids=${encodeURIComponent(q)}`
    );
    return response.data?.keys || {};
  },

  updateMessage: async (messageId, messageData) => {
    const response = await api.patch(`/messaging/messages/${messageId}/`, messageData);
    return response.data;
  },

  deleteMessage: async (messageId) => {
    await api.delete(`/messaging/messages/${messageId}/`);
  },

  // Attachments
  attachMessagePhotos: async (messageId, attachments) => {
    const response = await api.post(`/messaging/messages/${messageId}/attachments/`, {
      attachments,
    });
    return response.data;
  },

  // Reactions
  toggleReaction: async (messageId, emoji) => {
    const response = await api.post('/messaging/reactions/toggle_reaction/', {
      message_id: messageId,
      emoji: emoji
    });
    return response.data;
  },

  getReactions: async (messageId) => {
    const response = await api.get(`/messaging/reactions/?message_id=${messageId}`);
    return response.data;
  },

  // User Keys
  getUserKeys: async () => {
    const response = await api.get('/messaging/user-keys/');
    return response.data;
  },

  createUserKey: async (keyData) => {
    const response = await api.post('/messaging/user-keys/', keyData);
    return response.data;
  },

  updateUserKey: async (keyId, keyData) => {
    const response = await api.patch(`/messaging/user-keys/${keyId}/`, keyData);
    return response.data;
  },

  deleteUserKey: async (keyId) => {
    await api.delete(`/messaging/user-keys/${keyId}/`);
  },

  // Club Announcements
  createClubAnnouncement: async (clubId, announcementData) => {
    const response = await api.post(`/messaging/clubs/${clubId}/send-announcement/`, announcementData);
    return response.data;
  },

  // Direct Messages
  createDirectMessage: async (recipientId, messageData) => {
    const response = await api.post(`/messaging/users/${recipientId}/messages/`, messageData);
    return response.data;
  },

  findDirectMessage: async (userId) => {
    const response = await api.get(`/messaging/conversations/find_direct_message/?user_id=${userId}`);
    return response.data;
  },

  // Group Messages
  createGroupMessage: async (participantIds, messageData) => {
    const response = await api.post('/messaging/groups/', {
      participants: participantIds,
      ...messageData
    });
    return response.data;
  },

  // Utility functions
  markMessageAsRead: async (messageId) => {
    const response = await api.post(`/messaging/messages/${messageId}/mark_read/`);
    return response.data;
  },

  markConversationAsRead: async (conversationId) => {
    const response = await api.post(`/messaging/conversations/${conversationId}/mark_read/`);
    return response.data;
  },

  getUnreadTotal: async () => {
    const response = await api.get('/messaging/conversations/unread_total/');
    return response.data;
  },

  // Announcements
  getRecentAnnouncements: async (limit = 5) => {
    const response = await api.get(`/messaging/conversations/recent_announcements/?limit=${limit}`);
    return response.data;
  },

  // Search
  searchMessages: async (query, conversationId = null) => {
    const params = { q: query };
    if (conversationId) {
      params.conversation = conversationId;
    }
    const response = await api.get('/messaging/search/', { params });
    return response.data;
  },

  // Typing indicators
  sendTypingIndicator: async (conversationId, isTyping) => {
    await api.post(`/messaging/conversations/${conversationId}/typing/`, {
      is_typing: isTyping
    });
  },

  // New method for end hunt requests
  sendEndHuntRequest: async (recipientId, clubId, locationName) => {
    try {
      console.log('🔍 Looking for existing conversation with user:', recipientId);
      const existingConversation = await messagingService.findDirectMessage(recipientId);
      console.log('📋 Existing conversation result:', existingConversation);

      const messageContent = `Howdy. Don't forget to sign back in from your hunt at ${locationName}. [View Club Details](/clubs/${clubId})`;

      if (existingConversation.conversation) {
        console.log('✅ Using existing conversation:', existingConversation.conversation.id);
        const convRes = await api.get(
          `/messaging/conversations/${existingConversation.conversation.id}/`
        );
        return sendEncryptedUserMessage(messagingService, convRes.data, messageContent, 'text');
      } else {
        console.log('🆕 No existing conversation, creating DM and sending...');
        const created = await messagingService.createConversation({
          conversation_type: 'direct',
          participant_ids: [recipientId],
        });
        return sendEncryptedUserMessage(messagingService, created, messageContent, 'text');
      }
    } catch (error) {
      console.error('❌ Error in sendEndHuntRequest:', error);
      throw error;
    }
  },
};
